import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { ReservationServiceData } from '@app/res-common/models/rescom-service.model';
import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { ReservationApiAirLineList } from '../models/res-air.model';
import { IResApiGetOperationalDocumentParams, IResApiManageOperationalDocumentParams, ResBkgOperationalDocGuest, ResBkgOperationalDocServiceOption, ResBkgOperationalDocument, ResBookingMarginCalculatorDetail, IResApiManageBkgAdditionalExpParams } from '../models/res-booking.model';
import { IReservationApiCloneServices, IReservationApiGetOptions } from '../models/res-option.models';
import { ResServiceEMCDetails } from "../models/res-package-detail.model";
import { IResApiPackageSupplierRequest, ResPackageSupplierEmailRequestURL } from '../models/res-package.model';
import { ReservationSetupOption, ReservationSetupSequenceDiscount, ReservationSetupServiceOptionInclusiveDetail, ReservationServiceBookingReport, ReservationServicePackageListElement, ReservationServiceExperienceListElement, IResApiGetServiceOptionDayByDayParams, ResServiceOptionDayByDay, IResApiManageServiceOptionDayByDayParams, ResServiceConnectedList } from '../models/res-service.models';
import { ResBkgExperienceManageChoicesApiParams } from '@app/res-common/models/res-experience.model';

@Injectable({
  providedIn: 'root'
})
export class ReservationServiceSetupApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  private localSort(items: [], loadOptions: any): [] {
    if (!loadOptions?.sort?.length) return items;
    return items.sort((i1, i2) => {
      let field = loadOptions.sort[0].selector;
      let desc = loadOptions.sort[0].desc;
      let item1 = i1[field];
      if (!item1) {
        item1 = i1[this.titleCaseWord(field)];
      }
      let item2 = i2[field];
      if (!item2) {
        item2 = i2[this.titleCaseWord(field)];
      }
      if (item1 > item2) {
        return desc ? -1 : 1;
      }
      if (item1 < item2) {
        return desc ? 1 : -1;
      }
      return 0;
    });
  }

  private titleCaseWord(word: string) {
    if (!word) return word;
    return word[0].toUpperCase() + word.substring(1);
  }

  public cloneServices(params?: IReservationApiCloneServices): Observable<ReservationServiceData> {
    return this.postDynamicAPI('service/clone', params).pipe(
      map<DynamicApiResponse, ReservationServiceData>((response) => {
        return new ReservationServiceData(response.body);
      })
    );
  }

  experienceManageChoices(params: ResBkgExperienceManageChoicesApiParams) {
    return this.postDynamicAPI('experience/manageChoices', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    );
  }

  getAirlinesList(): Observable<ReservationApiAirLineList[]> {
    return this.callDynamicAPI('lookup/getAirlines', {}).pipe(
      map<DynamicApiResponse, ReservationApiAirLineList[]>((response) => {
        return response.body.map((data: any) => new ReservationApiAirLineList(data));
      })
    )
  }

  public getMarginCalculatorDetail(params: { MarginCalculatorParameterID: number, ItemID: number }) {
    return this.callDynamicAPI('booking/getMarginCalculatorDetail', params, 'GET').pipe(
      map<DynamicApiResponse, ResBookingMarginCalculatorDetail[]>((response) =>
        (response.body ?? []).map((data: any) => new ResBookingMarginCalculatorDetail(data))
      )
    );
  }

  getOperationalDocument(params: IResApiGetOperationalDocumentParams): Observable<{ operational: ResBkgOperationalDocument[], serviceOptions: ResBkgOperationalDocServiceOption[], guests: ResBkgOperationalDocGuest[] }> {
    return this.callDynamicAPI('booking/getItemLinkedSupplement', params).pipe(
      map<DynamicApiResponse, { operational: ResBkgOperationalDocument[], serviceOptions: ResBkgOperationalDocServiceOption[], guests: ResBkgOperationalDocGuest[] }>((response) => {
        return {
          operational: response.body.DT0.map((data: any) => (new ResBkgOperationalDocument(data))),
          serviceOptions: response.body.DT1.map((data: any) => (new ResBkgOperationalDocServiceOption(data))),
          guests: response.body.DT2.map((data: any) => (new ResBkgOperationalDocGuest(data)))
        }
      })
    );
  }

  public getSequentialDiscountCods(companyId: number): Observable<string[]> {
    const params = { CompanyID: companyId }
    return this.callDynamicAPI('sequentialDiscount/getOptionCod', params).pipe(
      map<DynamicApiResponse, Array<string>>((response) => {
        return response.body;
      })
    );
  }

  getServiceBookingReport(params: { ServiceID: number, ServiceOptionID?: number }): Observable<ReservationServiceBookingReport[]> {
    return this.callDynamicAPI('service/getServiceBookingReport', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationServiceBookingReport(el)))
    )
  }

  getServiceConnectedList(optionID: number): Observable<ResServiceConnectedList[]> {
    return this.callDynamicAPI('service/getConnectedService', { OptionID: optionID }).pipe(
      map<DynamicApiResponse, ResServiceConnectedList[]>((response) => response.body.map((data: any) => new ResServiceConnectedList(data)))
    );
  }

  getServiceECM(serviceID?: number): Observable<ResServiceEMCDetails[]> {
    return this.getDynamicAPI('bkg/getServiceECM', { ServiceID: serviceID }).pipe(
      map<DynamicApiResponse, ResServiceEMCDetails[]>((response) => {
        return response.body.map((data: any) => new ResServiceEMCDetails(data))
      })
    );
  }

  getServiceExperienceList(params: { ServiceID: number, OptionID?: number }): Observable<ReservationServiceExperienceListElement[]> {
    return this.callDynamicAPI('service/getExperience', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationServiceExperienceListElement(el)))
    )
  }

  getServiceOptionDayByDay(params: IResApiGetServiceOptionDayByDayParams): Observable<ResServiceOptionDayByDay[]> {
    return this.callDynamicAPI('service/getServiceOptDayByDay', params).pipe(
      map<DynamicApiResponse, ResServiceOptionDayByDay[]>((response) => {
        const header = response.body.DT0 ?? []
        const details = response.body.DT1 ?? []
        return header.map((v: any) => {
          v.DayItinerary = details.filter((d: any) => d.ServiceDayByDayID === v.ServiceDayByDayID)
          return new ResServiceOptionDayByDay(v)
        })
      })
    );
  }

  getServicePackageList(params: { ServiceID: number, OptionID?: number }): Observable<ReservationServicePackageListElement[]> {
    return this.callDynamicAPI('service/getServicePackageList', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationServicePackageListElement(el)))
    )
  }

  public getServicesOptionsIncludedDetail(serviceID: number, optionID: number): Observable<ReservationSetupServiceOptionInclusiveDetail[]> {
    return this.callDynamicAPI('option/selectDetail', { serviceID: serviceID, optionID: optionID }).pipe(
      map<DynamicApiResponse, Array<ReservationSetupServiceOptionInclusiveDetail>>((response) => {
        response.body.forEach((o: any) => {
          if (!o.Status) o.Status = 'A';
        });
        return response.body.map((o: any) => new ReservationSetupServiceOptionInclusiveDetail(o));
      })
    );
  }

  public getSetupOptions(params?: IReservationApiGetOptions): Observable<ReservationSetupOption[]> {
    return this.callDynamicAPI('option/get', params).pipe(
      map<DynamicApiResponse, Array<ReservationSetupOption>>((response) => {
        return response.body.map((o: any) => new ReservationSetupOption(o));
      })
    );
  }

  public getSetupOptionsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ReservationSetupOption>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('option/get', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationSetupOption>>((response: DynamicApiResponse) => {
          return {
            data: this.localSort(response.body, loadOptions).map((o: any) => new ReservationSetupOption(o)),
            totalCount: response.recordsCount
          }
        })
      )
    ).catch(() => {
      throw 'Data loading error';
    });
  }

  public getSetupSequenceDiscountsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ReservationSetupOption>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('option/get', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationSetupOption>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ReservationSetupSequenceDiscount(o)),
            totalCount: response.recordsCount
          };
        })
      )
    ).catch(() => {
      throw 'Data loading error';
    });
  }

  getSupplierEmailRequestURL(params: IResApiPackageSupplierRequest): Observable<ResPackageSupplierEmailRequestURL[]> {
    return this.getDynamicAPI('supplier/getSupplierRequestEmailURL', params).pipe(
      map<DynamicApiResponse, ResPackageSupplierEmailRequestURL[]>((response) => {
        return response.body.map((data: any) => new ResPackageSupplierEmailRequestURL(data))
      })
    );
  }

  manageBkgAdditionalExp(params: IResApiManageBkgAdditionalExpParams): Observable<any> {
    return this.postDynamicAPI('booking/manageBkgAdditionalExp', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageOperationalDocument(params: IResApiManageOperationalDocumentParams): Observable<any> {
    return this.postDynamicAPI('booking/manageItemLinkedSupplement', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  public manageOption(params?: any): Observable<any> {
    params.refundable = !!params.refundable ? 'Y' : 'N';
    params.commissionable = !!params.commissionable ? 'Y' : 'N';
    params.isSBC = !!params.isSBC ? 'Y' : 'N';
    params.isDayRoom = !!params.isDayRoom ? 'Y' : 'N';
    return this.postDynamicAPI('setupServiceOption/manage', params);
  }

  public manageSequentialDiscounts(params?: any) {
    params.refundable = !!params.refundable ? 'Y' : 'N';
    params.commissionable = !!params.commissionable ? 'Y' : 'N';
    params.isSBC = !!params.isSBC ? 'Y' : 'N';
    return this.postDynamicAPI('setupServiceOption/manage', params);
  }

  public manageServiceOption(params?: any): Observable<any> {
    return this.postDynamicAPI('setupServiceOptionLink/manage', params);
  }

  manageServiceOptionDayByDay(params?: IResApiManageServiceOptionDayByDayParams): Observable<any> {
    return this.postDynamicAPI('service/manageServiceOptDayByDay', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  public manageServiceOptionInclusive(params?: any): Observable<any> {
    return this.postDynamicAPI('setupServiceOptionInclusive/manage', params);
  }
}
